import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import type { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { getCookie, deleteCookie } from "../utils/cookies.ts";
import { apiClient } from "./apiClient.ts";
import AuthClientStore from "./authClientStore.ts";
import { captureReferralCode, clearReferralCode } from "./referralStore.ts";

// User type definition
export interface User {
  id: string;
  name: string; // Keep name as required with proper fallback mapping
  email: string;
  roles: string[]; // Keep roles as required but provide default
  permissions: string[]; // Keep permissions as required but provide default
  stripeCustomerId: string;
  emailsSubscribed: boolean;
  // Add other user properties as needed
}

// Authentication context type
export interface AuthenticationContextType {
  user: User | null;
  /** Email/password login against the shared auth backend. Throws `ApiError`
   * on failure (wrong credentials, validation errors) so the caller can show
   * a specific message. */
  login: (email: string, password: string) => Promise<void>;
  /** Exchanges a Google Identity Services access token for a session. */
  loginWithGoogle: (googleAccessToken: string) => Promise<void>;
  /** Exchanges a Facebook Login access token for a session. */
  loginWithFacebook: (facebookAccessToken: string) => Promise<void>;
  /** Exchanges an Apple authorization code for a session. */
  loginWithApple: (appleAuthorizationCode: string) => Promise<void>;
  /** Registers a new account. Resolves `{ requiresVerification: true }` when
   * the backend requires e-mail confirmation before the account can log in
   * (no session is created in that case); otherwise the user is logged in
   * immediately. Throws `ApiError` on validation failures. */
  register: (
    email: string,
    password1: string,
    password2: string
  ) => Promise<{ requiresVerification: boolean }>;
  logOut: () => void;
  refreshAuth: () => Promise<boolean>;
  requireAuth: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Create the authentication context
export const AuthenticationContext =
  createContext<AuthenticationContextType | null>(null);

// Provider props
interface AuthenticationProviderProps {
  children: ReactNode;
}

// Authentication Provider component
export const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Remember a referral link's `?ref=` on whatever page it lands on, so it is
  // still around when the visitor eventually signs up (see referralStore).
  useEffect(() => {
    captureReferralCode(location.search);
  }, [location.search]);

  // Function to refresh authentication using refresh token
  const refreshAuth = useCallback(async (): Promise<boolean> => {
    try {
      const refreshToken = getCookie("refreshToken");

      if (!refreshToken) {
        console.log("No refresh token found");
        return false;
      }

      console.log("Attempting to refresh authentication...");

      // Try to get current user (this will automatically refresh tokens if needed via apiClient)
      const response = await apiClient.getCurrentUser();

      if (response.data) {
        // Map the API response to our User interface
        const userData: User = {
          id: response.data.pk,
          name:
            response.data.name ||
            response.data.first_name ||
            response.data.email.split("@")[0],
          email: response.data.email,
          roles: response.data.roles || [],
          permissions: response.data.permissions || [],
          stripeCustomerId: response.data.stripe_customer_id || "",
          emailsSubscribed: response.data.emails_subscribed || false,
        };

        setUser(userData);
        console.log("Authentication refreshed successfully:", userData);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Authentication refresh failed:", error);
      setUser(null);
      return false;
    }
  }, []);

  // Function to check for existing access token and fetch user details
  const checkExistingAuth = useCallback(
    async (requireAuth: boolean = false): Promise<void> => {
      setIsLoading(true);
      try {
        const accessToken = getCookie("accessToken");
        const refreshToken = getCookie("refreshToken");

        if (accessToken || refreshToken) {
          console.log("Tokens found, attempting authentication...");

          // Try to refresh authentication (will use existing access token or refresh if needed)
          const authSuccess = await refreshAuth();

          if (!authSuccess && refreshToken) {
            console.log(
              "Initial auth failed, but refresh token exists. Clearing invalid tokens."
            );
            // If we have a refresh token but still failed, the refresh token might be invalid
            // Clear tokens to prevent infinite retry loops
            deleteCookie("accessToken");
            deleteCookie("refreshToken");
          }
        } else if (requireAuth) {
          console.log(
            "No tokens found and authentication required, redirecting to local login"
          );
          // Only redirect if authentication is explicitly required. Reads
          // window.location directly (rather than depending on the `location`
          // from useLocation) so this callback doesn't get recreated - and
          // this mount-only effect re-fired - on every route change.
          navigate("/login", {
            state: { from: { pathname: window.location.pathname } },
          });
          return; // Early return to prevent setting isLoading to false
        } else {
          console.log(
            "No tokens found, but authentication not required - continuing as unauthenticated user"
          );
          // Allow unauthenticated access
          setUser(null);
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    },
    [refreshAuth, navigate]
  );

  // Check for existing authentication on component mount
  useEffect(() => {
    checkExistingAuth();
  }, [checkExistingAuth]);

  // Load Smartsupp chat and set Sentry user info when user authenticates
  useEffect(() => {
    if (user) {
      // SECURITY: Only load Smartsupp for authenticated users (privacy-first approach)
      if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).loadSmartsupp) {
        console.log('Loading Smartsupp chat for authenticated user');
        ((window as unknown as Record<string, unknown>).loadSmartsupp as () => void)();
      }

      // Set Sentry user context for error tracking
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
      console.log('Sentry user context set:', user.id);
    } else {
      // Clear Sentry user context when user logs out or becomes unauthenticated
      Sentry.setUser(null);
    }
  }, [user]);

  // Email/password login against the shared auth backend.
  const login = async (email: string, password: string): Promise<void> => {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User submitted email/password login',
      level: 'info',
    });

    const response = await apiClient.login(email, password);
    AuthClientStore.setAccessToken(response.data.access);
    AuthClientStore.setRefreshToken(response.data.refresh);
    clearReferralCode();
    await refreshAuth();
  };

  // Google Identity Services login - exchanges their access token for ours.
  const loginWithGoogle = async (googleAccessToken: string): Promise<void> => {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User signed in with Google',
      level: 'info',
    });

    const response = await apiClient.loginWithGoogle(googleAccessToken);
    AuthClientStore.setAccessToken(response.data.access_token);
    AuthClientStore.setRefreshToken(response.data.refresh_token);
    clearReferralCode();
    await refreshAuth();
  };

  // Facebook Login - exchanges their access token for ours.
  const loginWithFacebook = async (facebookAccessToken: string): Promise<void> => {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User signed in with Facebook',
      level: 'info',
    });

    const response = await apiClient.loginWithFacebook(facebookAccessToken);
    AuthClientStore.setAccessToken(response.data.access_token);
    AuthClientStore.setRefreshToken(response.data.refresh_token);
    clearReferralCode();
    await refreshAuth();
  };

  // Sign in with Apple - the backend swaps the one-time authorization code
  // for tokens against Apple, then answers with ours.
  const loginWithApple = async (appleAuthorizationCode: string): Promise<void> => {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User signed in with Apple',
      level: 'info',
    });

    const response = await apiClient.loginWithApple(appleAuthorizationCode);
    AuthClientStore.setAccessToken(response.data.access_token);
    AuthClientStore.setRefreshToken(response.data.refresh_token);
    clearReferralCode();
    await refreshAuth();
  };

  // Registers a new account. Some backends log the user in immediately
  // (tokens come back in the response); others require e-mail verification
  // first, in which case only a `detail` message comes back and there is no
  // session to establish yet.
  const register = async (
    email: string,
    password1: string,
    password2: string
  ): Promise<{ requiresVerification: boolean }> => {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User submitted registration form',
      level: 'info',
    });

    const response = await apiClient.register(email, password1, password2);
    // The account exists now and the backend has recorded the referrer, even
    // if e-mail verification is still pending.
    clearReferralCode();
    const access = response.data.access ?? response.data.access_token;
    const refresh = response.data.refresh ?? response.data.refresh_token;

    if (access && refresh) {
      AuthClientStore.setAccessToken(access);
      AuthClientStore.setRefreshToken(refresh);
      await refreshAuth();
      return { requiresVerification: false };
    }

    return { requiresVerification: true };
  };

  // Function to require authentication - sends the user to the local login
  // page (rather than the ANP host) if they're not signed in.
  const requireAuth = useCallback(async (): Promise<void> => {
    if (!user) {
      console.log("Authentication required, redirecting to login...");
      navigate("/login", { state: { from: location } });
    }
  }, [user, navigate, location]);

  // Logout function - clears tokens and user state
  const logOut = (): void => {
    try {
      console.log("User logged out");

      // Clear both access and refresh token cookies
      deleteCookie("accessToken");
      deleteCookie("refreshToken");

      // Clear Sentry user context
      Sentry.setUser(null);

      // Clear user state
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const isAuthenticated = user !== null;

  const value: AuthenticationContextType = {
    user,
    login,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    register,
    logOut,
    refreshAuth,
    requireAuth,
    isAuthenticated,
    isLoading,
  };

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;

// Custom hook to use the authentication context
export const useAuthentication = (): AuthenticationContextType => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error(
      "useAuthentication must be used within an AuthenticationProvider"
    );
  }
  return context;
};

// Custom hook to require authentication on component mount
export const useRequireAuth = (): void => {
  const { requireAuth } = useAuthentication();

  useEffect(() => {
    requireAuth();
  }, [requireAuth]);
};
